import { useEffect, useState } from 'react'
import Layout from '@/components/Layout'
import { fetchPosts, deletePost } from '@/lib/api'
import type { Post } from '@/lib/types'

export default function PostsList() {
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [deleting, setDeleting] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    try { setPosts(await fetchPosts()) } catch { /* noop */ }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  async function handleDelete(id: string, title: string) {
    if (!confirm(`Delete post "${title}"?`)) return
    setDeleting(id)
    try { await deletePost(id); await load() } catch { /* noop */ }
    setDeleting(null)
  }

  const filtered = posts.filter(p =>
    !search ||
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    (p.category || '').toLowerCase().includes(search.toLowerCase())
  )

  const catStyles: Record<string, string> = {
    Guides: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    Export: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Business: 'bg-amber-50 text-amber-700 border-amber-200',
    News: 'bg-sky-50 text-sky-700 border-sky-200',
  }

  return (
    <Layout title="Blog"
      action={<a href="/blog/new" className="inline-flex items-center gap-2 bg-brand-600 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-brand-700 transition-colors">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
        New Post
      </a>}
    >
      <div className="max-w-5xl mx-auto space-y-5">
        <div className="relative max-w-md">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search posts..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-surface-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
        </div>

        <div className="space-y-3">
          {loading ? (
            <p className="text-slate-400 text-sm text-center py-10">Loading...</p>
          ) : filtered.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-10">No posts found.</p>
          ) : filtered.map(p => (
            <div key={p.id} className="bg-white rounded-2xl border border-surface-200 shadow-card hover:shadow-card-hover transition-all p-5 flex items-start gap-4">
              {p.cover_url ? (
                <img src={p.cover_url} alt="" className="w-24 h-20 rounded-xl object-cover border border-surface-200 shrink-0" onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
              ) : (
                <div className="w-24 h-20 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center text-white shrink-0">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M4 5a2 2 0 012-2h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm4 3h8m-8 4h8m-8 4h5"/></svg>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {p.category && (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${catStyles[p.category] ?? catStyles.Guides}`}>{p.category}</span>
                  )}
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${p.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${p.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    {p.is_active ? 'PUBLISHED' : 'DRAFT'}
                  </span>
                </div>
                <h3 className="font-semibold text-slate-900 text-sm leading-snug truncate">{p.title}</h3>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.excerpt || '—'}</p>
                <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-400">
                  <span>{p.author || 'PartsMarket UAE'}</span>
                  {p.published_at && <span>· {new Date(p.published_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <a href={`/blog/${p.id}`} className="text-xs font-semibold text-brand-600 hover:text-brand-700">Edit</a>
                <button onClick={() => handleDelete(p.id, p.title)} disabled={deleting === p.id}
                  className="text-xs font-semibold text-red-500 hover:text-red-600 disabled:opacity-50">
                  {deleting === p.id ? '...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  )
}
